import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Tab {
  id: string;
  label: string;
}

interface MachineTabState {
  tabs: Tab[];
  activeTabId: string;
}

interface DashboardTabsStore {
  // Machine-specific tab states
  machineTabStates: Record<string, MachineTabState>;
  
  // Actions
  initializeMachineTabState: (machineId: string, initialTabs: Tab[], activeTabId?: string) => void;
  getMachineTabState: (machineId: string) => MachineTabState | null;
  setActiveTab: (machineId: string, tabId: string) => void;
  addTab: (machineId: string, tab: Tab, position?: number) => void;
  removeTab: (machineId: string, tabId: string) => void;
  moveTab: (machineId: string, tabId: string, newIndex: number) => void;
  setTabs: (machineId: string, tabs: Tab[]) => void;
  
  // Hydration state
  hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

export const useDashboardTabsStore = create<DashboardTabsStore>()(
  persist(
    (set, get) => ({
      machineTabStates: {},
      hasHydrated: false,
      
      setHasHydrated: (state) => set({ hasHydrated: state }),
      
      initializeMachineTabState: (machineId, initialTabs, activeTabId = initialTabs[0]?.id || '') => {
        const state = get();
        if (!state.machineTabStates[machineId]) {
          set({
            machineTabStates: {
              ...state.machineTabStates,
              [machineId]: {
                tabs: initialTabs,
                activeTabId: activeTabId
              }
            }
          });
        }
      },
      
      getMachineTabState: (machineId) => {
        const state = get();
        return state.machineTabStates[machineId] || null;
      },
      
      setActiveTab: (machineId, tabId) => {
        const state = get();
        const machineState = state.machineTabStates[machineId];
        if (machineState) {
          set({
            machineTabStates: {
              ...state.machineTabStates,
              [machineId]: {
                ...machineState,
                activeTabId: tabId
              }
            }
          });
        }
      },
      
      addTab: (machineId, tab, position) => {
        const state = get();
        const machineState = state.machineTabStates[machineId];
        if (machineState) {
          const newTabs = [...machineState.tabs];
          if (position === undefined || position < 0 || position > newTabs.length) {
            newTabs.push(tab);
          } else {
            newTabs.splice(position, 0, tab);
          }
          set({
            machineTabStates: {
              ...state.machineTabStates,
              [machineId]: {
                ...machineState,
                tabs: newTabs,
                activeTabId: tab.id // Set the newly added tab as active
              }
            }
          });
        }
      },
      
      removeTab: (machineId, tabId) => {
        const state = get();
        const machineState = state.machineTabStates[machineId];
        if (machineState) {
          const idx = machineState.tabs.findIndex(tab => tab.id === tabId);
          const newTabs = machineState.tabs.filter(tab => tab.id !== tabId);
          let newActiveTabId = machineState.activeTabId;
          
          // If closing the active tab, activate the previous one or first tab
          if (machineState.activeTabId === tabId) {
            if (newTabs.length === 0) {
              newActiveTabId = '';
            } else if (idx > 0) {
              newActiveTabId = newTabs[idx - 1].id;
            } else {
              newActiveTabId = newTabs[0].id;
            }
          }
          
          set({
            machineTabStates: {
              ...state.machineTabStates,
              [machineId]: {
                ...machineState,
                tabs: newTabs,
                activeTabId: newActiveTabId
              }
            }
          });
        }
      },
      
      moveTab: (machineId, tabId, newIndex) => {
        const state = get();
        const machineState = state.machineTabStates[machineId];
        if (machineState) {
          const oldIndex = machineState.tabs.findIndex(tab => tab.id === tabId);
          if (oldIndex === -1 || newIndex < 0 || newIndex >= machineState.tabs.length) return;
          
          const newTabs = [...machineState.tabs];
          const [movedTab] = newTabs.splice(oldIndex, 1);
          newTabs.splice(newIndex, 0, movedTab);
          
          set({
            machineTabStates: {
              ...state.machineTabStates,
              [machineId]: {
                ...machineState,
                tabs: newTabs
              }
            }
          });
        }
      },
      
      setTabs: (machineId, tabs) => {
        const state = get();
        const machineState = state.machineTabStates[machineId];
        if (machineState) {
          // Ensure active tab is still valid
          const activeTabExists = tabs.some(tab => tab.id === machineState.activeTabId);
          set({
            machineTabStates: {
              ...state.machineTabStates,
              [machineId]: {
                ...machineState,
                tabs: tabs,
                activeTabId: activeTabExists ? machineState.activeTabId : (tabs[0]?.id || '')
              }
            }
          });
        }
      }
    }),
    {
      name: 'dashboard-tabs-store',
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      }
    }
  )
);
